import React, { useState, useEffect, useRef } from 'react';
import { Modal } from 'react-bootstrap';
import { AgGridReact } from 'ag-grid-react';
import { message, Spin, Tabs, Button } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import * as XLSX from 'xlsx';
import { 
  fetchOrdersWithoutMatches, 
  fetchTigerTechSalesWithoutMatches,
  fetchOrdersWithoutRealTransactions,
  fetchWebOrdersWithoutStripe
} from '../../../core/api/data';

const OrdersWithoutMatchesModal = ({ visible, onClose, filters, timezone, transactionType, merchantName }) => {
  const [activeTab, setActiveTab] = useState('sublytics');
  const [sublyticsData, setSublyticsData] = useState([]);
  const [tigerTechData, setTigerTechData] = useState([]);
  const [ordersWithoutTransactions, setOrdersWithoutTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const sublyticsGridRef = useRef(null);
  const tigerTechGridRef = useRef(null);
  const noTransactionsGridRef = useRef(null);
  
  // Determine if we're showing orders without real transactions modal
  const isRealTransactionsMode = transactionType && merchantName;
  
  // Determine if we're showing web orders without Stripe transactions
  const isWebOrdersStripeMode = transactionType === 'stripe' && merchantName?.includes('stripe');

  // Column definitions for orders without real transactions
  const ordersWithoutTransactionsColumnDefs = [
    { headerName: "#", field: "index", width: 70 },
    { headerName: "Order ID", field: "id", width: 120 },
    { headerName: "Credentials Name", field: "credentials_name", width: 150 },
    { headerName: "Campaign ID", field: "campaign_id", width: 120 },
    { 
      headerName: "Customer Name", 
      valueGetter: p => { 
        const f = p.data.customer?.first_name || ""; 
        const l = p.data.customer?.last_name || ""; 
        return `${f} ${l}`.trim(); 
      }, 
      width: 200 
    },
    { headerName: "Customer Email", valueGetter: p => p.data.customer?.email || "", width: 200 },
    { headerName: "Customer Phone", valueGetter: p => p.data.customer?.phone || "", width: 140 },
    { headerName: "Total Amount", field: "total_transaction_amount", width: 120 },
    { headerName: "Date Ordered", field: "date_ordered", width: 150 },
    { headerName: "Date Created", field: "date_created", width: 150 },
    { headerName: "Status", field: "status", width: 120 },
    { 
      headerName: "Last Transaction", 
      valueGetter: p => {
        if (!p.data.transactions || !p.data.transactions.length) return "";
        const lastTx = p.data.transactions[p.data.transactions.length - 1];
        return `${lastTx.processor_response_text || ""} (${lastTx.merchant?.merchant_name || ""})`;  
      },
      width: 250 
    }
  ];
  
  // Column definitions for Sublytics orders
  const sublyticsColumnDefs = [
    { headerName: "#", field: "index", width: 70 },
    { headerName: "Order ID", field: "id", width: 120 },
    { headerName: "Credentials Name", field: "credentials_name", width: 150 },
    { headerName: "Campaign ID", field: "campaign_id", width: 120 },
    { 
      headerName: "Customer Name", 
      valueGetter: p => { 
        const f = p.data.customer?.first_name || ""; 
        const l = p.data.customer?.last_name || ""; 
        return `${f} ${l}`.trim(); 
      }, 
      width: 200 
    },
    { headerName: "Customer Email", valueGetter: p => p.data.customer?.email || "", width: 200 },
    { headerName: "Customer Phone", valueGetter: p => p.data.customer?.phone || "", width: 140 },
    { headerName: "Total Amount", field: "total_transaction_amount", width: 120 },
    { headerName: "Date Ordered", field: "date_ordered", width: 150 },
    { headerName: "Date Created", field: "date_created", width: 150 },
    { headerName: "Status", field: "status", width: 120 }
  ];

  // Column definitions for Web Orders with Stripe payment method
  const webOrdersStripeColumnDefs = [
    { headerName: "#", field: "index", width: 70 },
    { headerName: "Order ID", field: "order_id", width: 120 },
    { headerName: "Number", field: "number", width: 120 },
    { 
      headerName: "Customer Name", 
      valueGetter: p => {
        const billing = p.data.billing || {};
        return `${billing.first_name || ''} ${billing.last_name || ''}`.trim();
      },
      width: 200 
    },
    { headerName: "Customer Email", valueGetter: p => p.data.billing?.email || "", width: 200 },
    { headerName: "Customer Phone", valueGetter: p => p.data.billing?.phone || "", width: 140 },
    { headerName: "Total Amount", field: "total", width: 120 },
    { headerName: "Payment Method", field: "payment_method", width: 150 },
    { headerName: "Transaction ID", field: "transaction_id", width: 200 },
    { headerName: "Date Paid", field: "date_paid", width: 150 },
    { headerName: "Date Created", field: "date_created", width: 150 },
    { headerName: "Status", field: "status", width: 120 },
  ];

  // Column definitions for TigerTech sales
  const tigerTechColumnDefs = [
    { headerName: "#", valueGetter: "node.rowIndex + 1", width: 70 },
    { headerName: "Order Number", field: "order_number", width: 120 },
    { headerName: "Merchant Transaction ID", field: "merchant_transaction_id", width: 180 },
    { 
      headerName: "Customer Name", 
      valueGetter: p => {
        const first = p.data.bill_first_name || "";
        const last = p.data.bill_last_name || "";
        return `${first} ${last}`.trim();
      },
      width: 200 
    },
    { headerName: "Phone", field: "bill_phone_number", width: 140 },
    { 
      headerName: "Address", 
      valueGetter: p => {
        const addr1 = p.data.bill_address1 || "";
        const addr2 = p.data.bill_address2 ? `, ${p.data.bill_address2}` : "";
        const city = p.data.bill_city ? `, ${p.data.bill_city}` : "";
        const state = p.data.bill_state ? `, ${p.data.bill_state}` : "";
        const zip = p.data.bill_zipcode ? ` ${p.data.bill_zipcode}` : "";
        return `${addr1}${addr2}${city}${state}${zip}`;
      },
      width: 250 
    },
    { headerName: "Amount", field: "amount_already_paid", width: 120 },
    { headerName: "Order Date", field: "order_date", width: 150 },
    { headerName: "Payment Type", field: "payment_type", width: 120 },
    { headerName: "Source", field: "source", width: 100 }
  ];

  // Fetch data when filters change or modal is opened
  useEffect(() => {
    if (visible && filters && filters.brands && filters.brands.length > 0) {
      if (isRealTransactionsMode) {
        fetchOrdersWithoutTransactionsData();
      } else {
        fetchData();
      }
    }
  }, [visible, filters, transactionType, merchantName]);
  
  // Fetch orders without real transactions
  const fetchOrdersWithoutTransactionsData = async () => {
    if (!filters || !filters.brands || filters.brands.length === 0) {
      message.error("Please select a brand");
      return;
    }
    
    setLoading(true);
    try {
      let response;
      
      // Use the appropriate API endpoint based on transaction type
      if (transactionType === 'stripe') {
        // For Stripe transactions, use the WebOrdersViewSet endpoint
        response = await fetchWebOrdersWithoutStripe(filters, merchantName);
      } else {
        // For Auth and NMI transactions, use the SublyticsOrdersViewSet endpoint
        response = await fetchOrdersWithoutRealTransactions(filters, transactionType, merchantName);
      }
      
      setOrdersWithoutTransactions(response.map((item, index) => ({
        ...item,
        index: index + 1
      })));
    } catch (error) {
      console.error('Error fetching orders without real transactions:', error);
      message.error('Failed to fetch orders');
      setOrdersWithoutTransactions([]);
    } finally {
      setLoading(false);
    }
  };

  // Fetch data for both tabs
  const fetchData = async () => {
    if (!filters || !filters.brands || filters.brands.length === 0) {
      message.error("Please select a brand");
      return;
    }

    setLoading(true);
    try {
      // Fetch Sublytics orders without TigerTech matches
      const sublyticsResponse = await fetchOrdersWithoutMatches(filters, 'missing_tigertech');
      setSublyticsData(sublyticsResponse.results.map((item, index) => ({
        ...item,
        index: index + 1
      })));

      // Fetch TigerTech sales without Sublytics matches
      const tigerTechResponse = await fetchTigerTechSalesWithoutMatches(filters);
      setTigerTechData(tigerTechResponse.results);
    } catch (error) {
      console.error("Error fetching orders without matches:", error);
      message.error("Failed to fetch orders without matches");
    } finally {
      setLoading(false);
    }
  };

  // Export data to CSV
  const exportToCSV = () => {
    // Handle orders without real transactions export
    if (isRealTransactionsMode) {
      const fileName = `orders_without_${transactionType}_transactions.xlsx`;
      let worksheet;
      
      // Handle different data structures based on transaction type
      if (isWebOrdersStripeMode) {
        // Format for web orders with Stripe payment method
        worksheet = XLSX.utils.json_to_sheet(ordersWithoutTransactions.map(order => {
          const billing = order.billing || {};
          
          return {
            'Order ID': order.order_id,
            'Number': order.number,
            'Customer Name': `${billing.first_name || ''} ${billing.last_name || ''}`.trim(),
            'Customer Email': billing.email || '',
            'Customer Phone': billing.phone || '',
            'Amount': order.total,
            'Payment Method': order.payment_method,
            'Transaction ID': order.transaction_id,
            'Date Paid': order.date_paid,
            'Date Created': order.date_created,
            'Status': order.status
          };
        }));
      } else {
        // Format for Sublytics orders (auth or NMI transactions)
        worksheet = XLSX.utils.json_to_sheet(ordersWithoutTransactions.map(order => {
          // Prepare customer data
          const customer = order.customer || {};
          const lastTransaction = order.transactions && order.transactions.length ? 
            order.transactions[order.transactions.length - 1] : {};
          
          return {
            'Order ID': order.id,
            'Credentials Name': order.credentials_name,
            'Campaign ID': order.campaign_id,
            'Customer Name': `${customer.first_name || ''} ${customer.last_name || ''}`.trim(),
            'Customer Email': customer.email || '',
            'Customer Phone': customer.phone || '',
            'Amount': order.total_transaction_amount,
            'Date Ordered': order.date_ordered,
            'Date Created': order.date_created,
            'Status': order.status,
            'Last Transaction Status': lastTransaction.processor_response_text || '',
            'Merchant Name': lastTransaction.merchant?.merchant_name || ''
          };
        }));
      }
      
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Orders Without Transactions");
      
      // Generate and download the file
      XLSX.writeFile(workbook, fileName);
      return;
    }
    
    // Original export logic for sublytics/tigertech
    const data = activeTab === 'sublytics' ? sublyticsData : tigerTechData;
    
    if (!data || data.length === 0) {
      message.warning("No data to export");
      return;
    }

    try {
      // Prepare data for export
      let exportData;
      
      if (activeTab === 'sublytics') {
        exportData = data.map(order => ({
          'Order ID': order.id,
          'Credentials Name': order.credentials_name,
          'Campaign ID': order.campaign_id,
          'Customer Name': `${order.customer?.first_name || ''} ${order.customer?.last_name || ''}`.trim(),
          'Customer Email': order.customer?.email || '',
          'Customer Phone': order.customer?.phone || '',
          'Total Amount': order.total_transaction_amount,
          'Date Ordered': order.date_ordered,
          'Date Created': order.date_created,
          'Status': order.status
        }));
      } else {
        exportData = data.map(sale => ({
          'Order Number': sale.order_number,
          'Merchant Transaction ID': sale.merchant_transaction_id,
          'Customer Name': sale.customer_name,
          'Customer Email': sale.customer_email,
          'Customer Phone': sale.customer_phone,
          'Amount': sale.amount_already_paid,
          'Order Date': sale.order_date,
          'Payment Type': sale.payment_type,
          'Source': sale.source
        }));
      }

      // Create worksheet
      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, activeTab === 'sublytics' ? 'Sublytics Orders' : 'TigerTech Sales');
      
      // Generate filename
      const fileName = activeTab === 'sublytics' 
        ? 'Sublytics_Orders_Without_TigerTech_Matches.xlsx' 
        : 'TigerTech_Sales_Without_Sublytics_Matches.xlsx';
      
      // Save file
      XLSX.writeFile(wb, fileName);
      message.success(`Exported to ${fileName}`);
    } catch (error) {
      console.error("Error exporting data:", error);
      message.error("Failed to export data");
    }
  };

  // Determine title based on mode
  const getModalTitle = () => {
    if (isRealTransactionsMode) {
      const transactionTypeDisplay = {
        'auth': 'Authorize.net',
        'stripe': 'Stripe',
        'nmi': 'NMI'
      }[transactionType] || transactionType;
      
      return `Orders Without ${transactionTypeDisplay} Transactions for ${merchantName}`;
    }
    return 'Orders Without Matches';
  };
  
  return (
    <Modal
      show={visible}
      onHide={onClose}
      size="xl"
      backdrop="static"
      keyboard={false}
      centered
    >
      <Modal.Header closeButton>
        <Modal.Title>{getModalTitle()}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {isRealTransactionsMode ? (
          <div>
            <div style={{display: 'flex', justifyContent: 'flex-end', marginBottom: '10px'}}>
              <Button 
                type="primary" 
                icon={<DownloadOutlined />}
                onClick={exportToCSV}
                disabled={loading || !ordersWithoutTransactions.length}
              >
                Export to Excel
              </Button>
            </div>
            <div className="ag-theme-quartz" style={{ width: "100%", height: "500px", position: 'relative' }}>
              {loading && (
                <div
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    backgroundColor: 'rgba(255, 255, 255, 0.7)',
                    zIndex: 1000,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                >
                  <Spin size="large" />
                  <div style={{ marginTop: '15px', fontWeight: 'bold' }}>Loading orders...</div>
                </div>
              )}
              <AgGridReact
                ref={noTransactionsGridRef}
                rowData={ordersWithoutTransactions}
                columnDefs={isWebOrdersStripeMode ? webOrdersStripeColumnDefs : ordersWithoutTransactionsColumnDefs}
                pagination={false}
                defaultColDef={{ sortable: true, filter: true, resizable: true }}
                onGridReady={(params) => {
                  noTransactionsGridRef.current = params;
                  if (params.api) {
                    params.api.sizeColumnsToFit();
                  }
                }}
              />
            </div>
          </div>
        ) : (
          <Tabs
          activeKey={activeTab}
          onChange={(key) => setActiveTab(key)}
          className="mb-3"
          tabBarExtraContent={{
            right: (
              <Button 
                type="primary" 
                icon={<DownloadOutlined />}
                onClick={exportToCSV}
                disabled={loading || (activeTab === 'sublytics' ? !sublyticsData.length : !tigerTechData.length)}
              >
                Export to Excel
              </Button>
            )
          }}
          items={[
            {
              key: 'sublytics',
              label: 'Sublytics Orders (Without TigerTech Match)',
              children: (
                <div className="ag-theme-quartz" style={{ width: "100%", height: "500px", position: 'relative' }}>
                  {loading && (
                    <div
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        backgroundColor: 'rgba(255, 255, 255, 0.7)',
                        zIndex: 1000,
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center',
                      }}
                    >
                      <Spin size="large" />
                      <div style={{ marginTop: '15px', fontWeight: 'bold' }}>Loading orders...</div>
                    </div>
                  )}
                  <AgGridReact
                    ref={sublyticsGridRef}
                    rowData={sublyticsData}
                    columnDefs={sublyticsColumnDefs}
                    pagination={false}
                    defaultColDef={{ sortable: true, filter: true, resizable: true }}
                    onGridReady={(params) => {
                      sublyticsGridRef.current = params;
                      if (params.api) {
                        params.api.sizeColumnsToFit();
                      }
                    }}
                  />
                </div>
              )
            },
            {
              key: 'tigertech',
              label: 'TigerTech Sales (Without Sublytics Match)',
              children: (
                <div className="ag-theme-quartz" style={{ width: "100%", height: "500px", position: 'relative' }}>
                  {loading && (
                    <div
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        backgroundColor: 'rgba(255, 255, 255, 0.7)',
                        zIndex: 1000,
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center',
                      }}
                    >
                      <Spin size="large" />
                      <div style={{ marginTop: '15px', fontWeight: 'bold' }}>Loading sales...</div>
                    </div>
                  )}
                  <AgGridReact
                    ref={tigerTechGridRef}
                    rowData={tigerTechData}
                    columnDefs={tigerTechColumnDefs}
                    pagination={false}
                    defaultColDef={{ sortable: true, filter: true, resizable: true }}
                    onGridReady={(params) => {
                      tigerTechGridRef.current = params;
                      if (params.api) {
                        params.api.sizeColumnsToFit();
                      }
                    }}
                  />
                </div>
              )
            }
          ]}
        />
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button onClick={onClose}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default OrdersWithoutMatchesModal;
